import { useMutation, useQuery } from '@apollo/client';

import {
  Center,
  Spinner,
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Text,
  VStack,
  useDisclosure,
  useToast,
  ButtonGroup,
} from '@chakra-ui/react';

import { Select } from "chakra-react-select";

import moment from 'moment';

import { omit } from 'underscore';

import { useCallback, useEffect } from 'react';

import { useForm, Controller } from 'react-hook-form';

import {
  ADD_MEMBER,
  ADMIN_GET_MEMBER,
  DELETE_MEMBER,
  GET_MEMBERS_BY_LAST_INITIAL,
  RESEND_MEMBER_WELCOME_EMAIL,
  UPDATE_MEMBER,
} from '../../../../Queries';

import { useUser } from '../../../../Context';

const memberGroupOptions = [
  {
    label: 'Admins',
    value: 'admins'
  },
  {
    label: 'Members',
    value: 'members'
  },
  {
    label: 'Match Directors',
    value: 'match_directors'
  }
];

const memberTypeOptions = [
  {
    label: 'Shotgun',
    value: 'SHOTGUN'
  },
  {
    label: 'Full',
    value: 'FULL'
  },
  {
    label: 'Spouse',
    value: 'SPOUSE'
  },
  {
    label: 'Family',
    value: 'FAMILY'
  },
  {
    label: 'Life',
    value: 'LIFE'
  },
  {
    label: 'Honorary',
    value: 'HONORARY'
  },
  {
    label: 'Inactive',
    value: 'INACTIVE'
  },
  {
    label: 'Banned',
    value: 'BANNED'
  },
  {
    label: 'Senior',
    value: 'SENIOR'
  },
  {
    label: 'ASWAT',
    value: 'ASWAT'
  },
  {
    label: 'GSWAT',
    value: 'GSWAT'
  },
  {
    label: 'DPD',
    value: 'DPD'
  }
];

const secondAmendmentOrgOptions = [
  { label: 'Colorado State Shooting Association (CSSA)', value: 'CSSA' },
  { label: 'Gun Owners of America (GOA)', value: 'GOA' },
  { label: 'National Association for Gun Rights (NAGR)', value: 'NAGR' },
  { label: 'National Rifle Association (NRA)', value: 'NRA' },
  { label: 'Second Amendment Foundation (SAF)', value: 'SAF' },
];

const orgMembershipTypeOptions = [
  { label: 'Annual', value: 'ANNUAL' },
  { label: 'Life', value: 'LIFE' },
];

export const EditMemberModal = ({ isOpen, onClose, openEditMember, setOpenEditMember }) => {
  const { user } = useUser();
  const toast = useToast();

  const { isOpen: deleteIsOpen, onOpen: deleteOnOpen, onClose: deleteOnClose } = useDisclosure();

  const { loading, error, data } = useQuery(ADMIN_GET_MEMBER, {
    skip: !openEditMember?.member_email && !openEditMember?.member_details?.member_email,
    variables: {
      member_email: openEditMember?.member_email || openEditMember?.member_details?.member_email
    }
  });

  const defaultValues = {
    member_first_name: '',
    member_middle_initial: '',
    member_last_name: '',
    member_email: '',
    member_phone_number: '',
    date_of_birth: null,
    gate_card: '',
    nra_id: '',
    second_amendment_org: '',
    org_membership_id: '',
    org_membership_type: '',
    org_expiration_date: null,
    last_renewal_date: null,
    paid_through: null,
    last_orientation_date: null,
    member_type: '',
    member_groups: [],
    member_since: null,
  }

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    defaultValues
  });

  const fillForm = useCallback(() => {
    const {
      member_first_name,
      member_middle_initial,
      member_last_name,
      member_email,
      member_phone_number,
      date_of_birth,
      gate_card,
      nra_id,
      second_amendment_org,
      org_membership_id,
      org_membership_type,
      org_expiration_date,
      last_renewal_date,
      paid_through,
      last_orientation_date,
      member_type,
      member_groups,
      member_since
    } = data?.adminGetMember?.member_details || {};
  
    setValue('member_first_name', member_first_name || '');
    setValue('member_middle_initial', member_middle_initial || '');
    setValue('member_last_name', member_last_name || '');
    setValue('member_email', member_email || '');
    setValue('member_phone_number', member_phone_number || '');
    setValue('date_of_birth', date_of_birth || null);
    setValue('gate_card', gate_card || '');
    setValue('nra_id', nra_id || '');
    setValue('second_amendment_org', second_amendment_org || '');
    setValue('org_membership_id', org_membership_id || '');
    setValue('org_membership_type', org_membership_type || '');
    setValue('org_expiration_date', org_expiration_date || null);
    setValue('last_renewal_date', last_renewal_date || null);
    setValue('paid_through', paid_through || null);
    setValue('last_orientation_date', last_orientation_date || null);
    setValue('member_type', member_type || '');
    setValue('member_groups', member_groups || '');
    setValue('member_since', member_since || null);
  }, [data]);

  const clearForm = () => {
    setValue('member_first_name', defaultValues?.member_first_name);
    setValue('member_middle_initial', defaultValues?.member_middle_initial);
    setValue('member_last_name', defaultValues?.member_last_name);
    setValue('member_email', defaultValues?.member_email);
    setValue('member_phone_number', defaultValues?.member_phone_number);
    setValue('date_of_birth', defaultValues?.date_of_birth);
    setValue('gate_card', defaultValues?.gate_card);
    setValue('nra_id', defaultValues?.nra_id);
    setValue('second_amendment_org', defaultValues?.second_amendment_org);
    setValue('org_membership_id', defaultValues?.org_membership_id);
    setValue('org_membership_type', defaultValues?.org_membership_type);
    setValue('org_expiration_date', defaultValues?.org_expiration_date);
    setValue('last_renewal_date', defaultValues?.last_renewal_date);
    setValue('paid_through', defaultValues?.paid_through);
    setValue('last_orientation_date', defaultValues?.last_orientation_date);
    setValue('member_type', defaultValues?.member_type);
    setValue('member_groups', defaultValues?.member_groups);
    setValue('member_since', defaultValues?.member_since);
  }

  const formData = watch();

  const createOptionString = (options) => {
    const values = options.map(({ value }) => value);
    return values.join(', ');
  }

  const getSelectedValues = (values, options) => {
    const splitString = values?.split(', ');
    const optionsArray = splitString.map((string) => {
      return options.find((option) => option.value.toString() === string);
    });
    const filteredArray = optionsArray.filter((option) => option);
    return filteredArray;
  }

  useEffect(() => {
    fillForm();
  }, [data]);

  const [addMember, { data: addMemberData, loading: addMemberLoading, error: addMemberError }] = useMutation(ADD_MEMBER, {
    skip: !user || (!formData?.member_first_name || !formData?.member_last_name || !formData?.member_email),
    variables: {
      public_only: user === undefined,
      member_details: {
        member_first_name: formData?.member_first_name || null,
        member_middle_initial: formData?.member_middle_initial || null,
        member_last_name: formData?.member_last_name || null,
        member_email: formData?.member_email || null,
        member_phone_number: formData?.member_phone_number || null,
        member_groups: Array.isArray(formData?.member_groups) ? formData?.member_groups : formData?.member_groups?.split(",").map(g => g.trim()),
        member_type: formData?.member_type || null,
        gate_card: formData?.gate_card || null,
        nra_id: formData?.nra_id || null,
        second_amendment_org: formData?.second_amendment_org || null,
        org_membership_id: formData?.org_membership_id || null,
        org_membership_type: formData?.org_membership_type || null,
        org_expiration_date: formData?.org_expiration_date || null,
        date_of_birth: formData?.date_of_birth || null,
        last_renewal_date: formData?.last_renewal_date || null,
        paid_through: formData?.paid_through || null,
        last_orientation_date: formData?.last_orientation_date || null,
        member_since: formData?.member_since || null,
      },
    },
    refetchQueries: [ADMIN_GET_MEMBER, GET_MEMBERS_BY_LAST_INITIAL],
    onCompleted: () => {
      toast({
        title: 'Member added.',
        description: "",
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      clearForm();
      setOpenEditMember({});
    },
    onError: () => {
      toast({
        title: 'Member not added.',
        description: "",
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  });

  const [updateMember, { data: updateMemberData, loading: updateMemberLoading, error: updateMemberError }] = useMutation(UPDATE_MEMBER, {
    skip: !user || !openEditMember?.member_email,
    variables: {
      public_only: user === undefined,
      member_details: {
        member_first_name: formData?.member_first_name || null,
        member_middle_initial: formData?.member_middle_initial || null,
        member_last_name: formData?.member_last_name || null,
        member_email: formData?.member_email || null,
        member_phone_number: formData?.member_phone_number || null,
        member_groups: Array.isArray(formData?.member_groups) ? formData?.member_groups : formData?.member_groups?.split(",").map(g => g.trim()),
        member_type: formData?.member_type || null,
        gate_card: formData?.gate_card || null,
        nra_id: formData?.nra_id || null,
        second_amendment_org: formData?.second_amendment_org || null,
        org_membership_id: formData?.org_membership_id || null,
        org_membership_type: formData?.org_membership_type || null,
        org_expiration_date: formData?.org_expiration_date || null,
        date_of_birth: formData?.date_of_birth || null,
        last_renewal_date: formData?.last_renewal_date || null,
        paid_through: formData?.paid_through || null,
        last_orientation_date: formData?.last_orientation_date || null,
        member_since: formData?.member_since || null,
      },
      member_email: openEditMember?.member_email || data?.adminGetMember?.member_email,
    },
    refetchQueries: [ADMIN_GET_MEMBER, GET_MEMBERS_BY_LAST_INITIAL],
    onCompleted: () => {
      toast({
        title: 'Member updated.',
        description: "",
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      clearForm();
      setOpenEditMember({});
    },
    onError: () => {
      toast({
        title: 'Member not updated.',
        description: "",
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  });

  const [deleteMember, { data: deleteMemberData, loading: deleteMemberLoading, error: deleteMemberError }] = useMutation(DELETE_MEMBER, {
    skip: !user || !openEditMember?.member_email,
    variables: {
      public_only: user === undefined,
      member_email: openEditMember?.member_email || data?.adminGetMember?.member_email,
    },
    refetchQueries: [GET_MEMBERS_BY_LAST_INITIAL],
    onCompleted: () => {
      toast({
        title: 'Member deleted.',
        description: "",
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      clearForm();
      deleteOnClose();
      setOpenEditMember({});
    },
    onError: () => {
      toast({
        title: 'Member not deleted.',
        description: "",
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      deleteOnClose();
    }
  });

  const [resendWelcomeEmail, { loading: resendEmailLoading }] = useMutation(RESEND_MEMBER_WELCOME_EMAIL, {
    onCompleted: (data) => {
      if (data.resendMemberWelcomeEmail.success) {
        toast({
          title: 'Welcome email sent!',
          description: data.resendMemberWelcomeEmail.message,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Failed to send welcome email',
          description: data.resendMemberWelcomeEmail.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Error sending welcome email',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  });

  const handleResendWelcomeEmail = () => {
    const memberEmail = openEditMember?.member_email || data?.adminGetMember?.member_email;
    if (memberEmail) {
      resendWelcomeEmail({
        variables: {
          member_email: memberEmail
        }
      });
    }
  };

  const onSubmit = (values) => {
    if (openEditMember?.member_email) {
      updateMember();
    } else {
      addMember();
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Member</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading ? (
              <Center mb={4}>
                <Spinner />
              </Center>
            ) : (
              <VStack mb={2} as="form" alignItems="start" onSubmit={handleSubmit(onSubmit)}>
                {openEditMember?.member_email && (
                  <FormControl>
                    <FormLabel htmlFor='cognito_user_id'>Cognito User ID (set during self-signup)</FormLabel>
                    <Input
                      id='cognito_user_id'
                      placeholder='cognito user id'
                      value={data?.adminGetMember?.member_details?.cognito_user_id || 'Not signed up yet'}
                      readOnly={true}
                    />
                  </FormControl>
                )}
                <FormControl isInvalid={errors.member_first_name}>
                  <FormLabel htmlFor='member_first_name'>First name *</FormLabel>
                  <Input
                    id='member_first_name'
                    placeholder='first name'
                    {...register('member_first_name', {
                      required: 'This is required',
                      minLength: { value: 2, message: 'Minimum length should be 2' },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.member_first_name && errors.member_first_name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor='member_middle_initial'>Middle Initial (optional)</FormLabel>
                  <Input
                    id='member_middle_initial'
                    placeholder='middle initial'
                    {...register('member_middle_initial', {})}
                  />
                </FormControl>
                <FormControl isInvalid={errors.member_last_name}>
                  <FormLabel htmlFor='member_last_name'>Last name *</FormLabel>
                  <Input
                    id='member_last_name'
                    placeholder='last name'
                    {...register('member_last_name', {
                      required: 'This is required',
                      minLength: { value: 2, message: 'Minimum length should be 2' },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.member_last_name && errors.member_last_name.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors.member_email}>
                  <FormLabel htmlFor='member_email'>Email * (used for self-signup validation)</FormLabel>
                  <Input
                    id='member_email'
                    placeholder='email'
                    {...register('member_email', {
                      required: 'This is required',
                      minLength: { value: 4, message: 'Minimum length should be 4' },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.member_email && errors.member_email.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={errors.member_phone_number}>
                  <FormLabel htmlFor='member_phone_number'>Phone number *</FormLabel>
                  <Input
                    id='member_phone_number'
                    placeholder='phone number'
                    {...register('member_phone_number', {
                      required: 'This is required',
                      minLength: { value: 4, message: 'Minimum length should be 4' },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.member_phone_number && errors.member_phone_number.message}
                  </FormErrorMessage>
                </FormControl>
                <Controller
                  control={control}
                  name="date_of_birth"
                  rules={{ required: "This is required" }}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) =>  (
                    <FormControl id="date_of_birth" isInvalid={errors.date_of_birth}>
                      <FormLabel>Date of birth *</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='date_of_birth'
                        placeholder='date of birth'
                      />
                      <FormErrorMessage>
                        {errors.date_of_birth && errors.date_of_birth.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="member_groups"
                  rules={{ required: "This is required" }}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                  }) => (
                    <FormControl isInvalid={errors.member_groups} id="member_groups">
                      <FormLabel>Member Groups *</FormLabel>
                      <Select
                        isMulti
                        name={name}
                        ref={ref}
                        onBlur={onBlur}
                        value={getSelectedValues(value?.toString(), memberGroupOptions)}
                        onChange={(values) => onChange(createOptionString(values))}
                        isOptionDisabled={() => false}
                        options={memberGroupOptions}
                        placeholder="member groups"
                        closeMenuOnSelect={false}
                      />
                      <FormErrorMessage>{errors.member_groups && errors.member_groups.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="member_type"
                  rules={{ required: "This is required" }}
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) => (
                    <FormControl isInvalid={errors.member_type} id="member_type">
                      <FormLabel>Membership Type *</FormLabel>
                      <Select
                        ref={ref}
                        name={name}
                        onBlur={onBlur}
                        onChange={(value) => onChange(value.value)}
                        options={memberTypeOptions}
                        placeholder="membership type"
                        value={memberTypeOptions.find((option) => option.value === value)}
                        // closeMenuOnSelect={false}
                      />

                      <FormErrorMessage>{errors.member_type && errors.member_type.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />
                <FormControl>
                  <FormLabel htmlFor='gate_card'>Gate Card (optional)</FormLabel>
                  <Input
                    type="number"
                    id='gate_card'
                    placeholder='gate card'
                    {...register('gate_card', {})}
                  />
                </FormControl>
                <Text color="teal.600" fontWeight="bold" fontSize="sm" mt={4}>
                  Approved Second Amendment Organization (optional)
                </Text>
                <Controller
                  control={control}
                  name="second_amendment_org"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                  }) => (
                    <FormControl id="second_amendment_org">
                      <FormLabel>Approved Second Amendment Organization</FormLabel>
                      <Select
                        ref={ref}
                        name={name}
                        onBlur={onBlur}
                        onChange={(val) => onChange(val ? val.value : '')}
                        options={secondAmendmentOrgOptions}
                        placeholder="Select organization..."
                        value={secondAmendmentOrgOptions.find((option) => option.value === value) || null}
                        isClearable
                      />
                    </FormControl>
                  )}
                />
                <FormControl>
                  <FormLabel htmlFor='org_membership_id'>Organization Membership ID (optional)</FormLabel>
                  <Input
                    id='org_membership_id'
                    placeholder='membership id'
                    {...register('org_membership_id', {})}
                  />
                </FormControl>
                <Controller
                  control={control}
                  name="org_membership_type"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                  }) => (
                    <FormControl id="org_membership_type">
                      <FormLabel>Organization Membership Type (optional)</FormLabel>
                      <Select
                        ref={ref}
                        name={name}
                        onBlur={onBlur}
                        onChange={(val) => onChange(val ? val.value : '')}
                        options={orgMembershipTypeOptions}
                        placeholder="Select membership type..."
                        value={orgMembershipTypeOptions.find((option) => option.value === value) || null}
                        isClearable
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="org_expiration_date"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                  }) =>  (
                    <FormControl id="org_expiration_date">
                      <FormLabel>Organization Expiration Date (optional)</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='org_expiration_date'
                        placeholder='expiration date'
                      />
                      <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">
                        If "Life" is selected as the membership type, an expiration date is not required.
                      </Text>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="member_since"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) =>  (
                    <FormControl id="member_since">
                      <FormLabel>Member since (optional - defaults to today)</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='member_since'
                        placeholder='member since'
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="last_renewal_date"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) =>  (
                    <FormControl id="last_renewal_date">
                      <FormLabel>Last renewal date (optional)</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='last_renewal_date'
                        placeholder='last renewal date'
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="paid_through"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) =>  (
                    <FormControl id="paid_through">
                      <FormLabel>Paid through (optional)</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='paid_through'
                        placeholder='paid through'
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="last_orientation_date"
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    // fieldState: { error }
                  }) =>  (
                    <FormControl id="last_orientation_date">
                      <FormLabel>Last orientation date (optional)</FormLabel>
                      <Input
                        type="date"
                        name={name}
                        value={value ? moment.utc(value * 1000).format('yyyy-MM-DD') : undefined}
                        onChange={(event) => onChange(event.target.value ? moment(event.target.value).unix(): "")}
                        id='last_orientation_date'
                        placeholder='last orientation date'
                        />
                    </FormControl>
                  )}
                />
                <ButtonGroup pt={2} flexWrap="wrap" spacing={2}>
                  <Button
                    type='submit'
                    colorScheme='teal' 
                    isLoading={
                      loading ||
                      isSubmitting ||
                      addMemberLoading ||
                      updateMemberLoading
                    } 
                    isDisabled={
                      loading ||
                      isSubmitting ||
                      addMemberLoading ||
                      updateMemberLoading
                    }
                  >
                    Submit
                  </Button>
                  {(openEditMember?.member_email || openEditMember?.member_details?.member_email) && (
                    <Button
                      onClick={deleteOnOpen}
                      colorScheme='red'
                      isLoading={
                        loading ||
                        deleteMemberLoading
                      } 
                      isDisabled={
                        loading ||
                        isSubmitting ||
                        addMemberLoading ||
                        updateMemberLoading ||
                        deleteMemberLoading
                      }
                    >
                      Delete
                    </Button>
                  )}
                  {(openEditMember?.member_email || openEditMember?.member_details?.member_email) && 
                   !data?.adminGetMember?.member_details?.cognito_user_id && (
                    <Button
                      onClick={handleResendWelcomeEmail}
                      colorScheme='blue'
                      variant='outline'
                      isLoading={resendEmailLoading}
                      isDisabled={
                        loading ||
                        isSubmitting ||
                        addMemberLoading ||
                        updateMemberLoading ||
                        deleteMemberLoading ||
                        resendEmailLoading
                      }
                    >
                      Resend Welcome Email
                    </Button>
                  )}
                  <Button onClick={onClose}>Cancel</Button>
                </ButtonGroup>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={deleteIsOpen} onClose={deleteOnClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are You Sure?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack>
              <ButtonGroup pt={2}>
                <Button
                  colorScheme='red'
                  onClick={deleteMember}
                  isLoading={deleteMemberLoading}
                  isDisabled={deleteMemberLoading}
                >
                  Delete
                </Button>
                <Button onClick={deleteOnClose}>Cancel</Button>
              </ButtonGroup>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
